import type { MeResponse } from '@siteday/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext } from 'react';
import { useNavigate } from 'react-router';

import { authApi, qk } from '@/api';
import { DEFAULT_TZ } from '@/lib/dates';

/** GET /auth/me on load; the cookie session decides whether we are signed in (doc 11). */
export function useMeQuery() {
  return useQuery({ queryKey: qk.me(), queryFn: authApi.me, retry: false, staleTime: 5 * 60_000 });
}

const SessionCtx = createContext<MeResponse | null>(null);
export const SessionProvider = SessionCtx.Provider;

/** The signed-in user and company basics; only valid under the AuthGuard. */
export function useSession(): MeResponse {
  const s = useContext(SessionCtx);
  if (!s) throw new Error('useSession outside AuthGuard');
  return s;
}

export function useCompanyBasics() {
  const s = useContext(SessionCtx);
  return {
    tz: s?.company.timezone ?? DEFAULT_TZ,
    cutoffTime: s?.company.cutoffTime ?? '19:00',
    unitsEnabled: s?.company.unitsEnabled ?? [],
    companyName: s?.company.name ?? 'SiteDay',
    logoUrl: s?.company.logoUrl ?? null,
    standardDayHours: s?.company.standardDayHours ?? '10',
  };
}

/**
 * Support impersonation (doc 13 "Support panel"): the API refuses every write with
 * 403, so the primary mutation buttons are disabled up front and a banner shows.
 */
export function useImpersonation(): {
  impersonating: boolean;
  supportAdminName: string | null;
  returnUrl: string | null;
} {
  const s = useContext(SessionCtx);
  return {
    impersonating: s?.impersonating === true,
    supportAdminName: s?.supportAdminName ?? null,
    returnUrl: s?.supportReturnUrl ?? null,
  };
}

export function useIsOffice(): boolean {
  const s = useContext(SessionCtx);
  return s?.user.role === 'owner' || s?.user.role === 'admin';
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: authApi.login,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.me() });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { returnUrl } = useImpersonation();
  return useMutation({
    mutationFn: authApi.logout,
    onSettled: () => {
      qc.clear();
      // A support session goes back to the Fuorix panel (SITEDAY_ADMIN_URL) instead of the tenant sign-in.
      if (returnUrl) window.location.assign(returnUrl);
      else navigate('/sign-in', { replace: true });
    },
  });
}
